import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ChartConfiguration, ChartType } from 'chart.js';

@Component({
  selector: 'app-chat-chart',
  templateUrl: './chat-chart.component.html',
  styleUrls: ['./chat-chart.component.scss'],
  standalone: false,
})
export class ChatChartComponent implements OnChanges {
  @Input() chartData: any;

  public chartType: ChartType = 'line';
  public chartLabels: string[] = [];
  public chartDatasets: any[] = [];
  public chartOptions: ChartConfiguration['options'] = {};

  private readonly colors = [
    '#1036D6', '#19c37d', '#F7B500', '#E02020', '#6583FF',
    '#FA6400', '#44D7B6', '#A653E2', '#0091FF', '#8A6500',
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && this.chartData) {
      this.buildChart();
    }
  }

  private buildChart(): void {
    const data = this.chartData;

    // Determine chart type
    const isBar = data.chart_type === 'bar';
    const isArea = data.chart_type === 'area';
    this.chartType = isBar ? 'bar' : 'line';

    // Labels (x-axis)
    this.chartLabels = data.x_axis?.values || data.x_values || data.labels || [];

    // Series → datasets
    const series = data.series || [];
    this.chartDatasets = series.map((s: any, i: number) => ({
      label: s.name || s.label || `Series ${i + 1}`,
      data: s.data || s.values || [],
      borderColor: this.colors[i % this.colors.length],
      backgroundColor: isBar
        ? this.hexToRgba(this.colors[i % this.colors.length], 0.7)
        : isArea
          ? this.hexToRgba(this.colors[i % this.colors.length], 0.15)
          : 'transparent',
      fill: isArea,
      tension: isBar ? 0 : 0.3,
      pointRadius: isBar ? 0 : 3,
      pointHoverRadius: isBar ? 0 : 5,
      borderWidth: isBar ? 0 : 2,
      borderRadius: isBar ? 4 : 0,
    }));

    // Options
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: !!data.title,
          text: data.title || '',
          font: { family: 'Montserrat', size: 14, weight: 'bold' as any },
          color: '#060B1E',
          padding: { bottom: 16 },
        },
        legend: {
          display: series.length > 1,
          position: 'bottom',
          labels: {
            font: { family: 'Montserrat', size: 12 },
            usePointStyle: true,
            padding: 16,
          },
        },
        tooltip: {
          backgroundColor: '#060B1E',
          titleFont: { family: 'Montserrat', size: 12 },
          bodyFont: { family: 'Montserrat', size: 12 },
          padding: 10,
          cornerRadius: 6,
        },
      },
      scales: {
        x: {
          title: {
            display: !!data.x_axis?.label,
            text: data.x_axis?.label || '',
            font: { family: 'Montserrat', size: 12 },
            color: '#60698B',
          },
          ticks: {
            font: { family: 'Montserrat', size: 11 },
            color: '#8B94B3',
            maxRotation: 45,
            autoSkip: true,
            maxTicksLimit: 15,
          },
          grid: { color: '#E9ECF4' },
        },
        y: {
          title: {
            display: !!data.y_axis?.label,
            text: data.y_axis?.label || '',
            font: { family: 'Montserrat', size: 12 },
            color: '#60698B',
          },
          ticks: {
            font: { family: 'Montserrat', size: 11 },
            color: '#8B94B3',
          },
          grid: { color: '#E9ECF4' },
          beginAtZero: true,
        },
      },
    };
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
